import { useRef, useState } from 'react'

/**
 * A row that slides left to uncover one destructive action.
 *
 * Three things this has to get right, none of them optional on a phone:
 *
 * 1. **Axis lock.** The first ~10px of a drag decide whether it is a swipe or a
 *    page scroll, and after that the other axis is ignored for the rest of the
 *    gesture. Without it, a slightly diagonal scroll drags rows open down the
 *    whole list.
 * 2. **The action is a real button**, present in the DOM whether or not the row
 *    is open. Swipe is the shortcut, not the only route: a keyboard reaches it
 *    by tabbing, and it stays visible once open rather than requiring a second
 *    blind gesture.
 * 3. **Rubber-banding past the stop** — dragging further than the action is
 *    wide resists instead of running away, which is what says "this is as far
 *    as it goes" without a bounce animation.
 *
 * Deliberately not a library. This is ~70 lines against a dependency that would
 * bring its own gesture system into an app that has no other use for one.
 */

const ACTION_W = 92   // must match .swipe-action width in styles.css
const THRESHOLD = 44  // past this on release, it settles open

export default function SwipeRow({ children, actionLabel = 'Delete', onAction, disabled }) {
  const [dx, setDx] = useState(0)
  const [open, setOpen] = useState(false)
  const [dragging, setDragging] = useState(false)
  const start = useRef(null)
  const axis = useRef(null) // null until decided, then 'x' | 'y'

  /**
   * The live offset, and the only thing touchend is allowed to read.
   *
   * State cannot serve here: React batches, so a move and the end that follows
   * it inside one tick leave `dx` still holding the previous render's value —
   * and touchend then measures a distance the finger already travelled past.
   * Across separate frames it happens to work, which is what makes the failure
   * so unpleasant: the row opens for a slow drag and silently refuses a quick
   * flick. The ref is the gesture; the state exists only to paint it.
   */
  const offset = useRef(0)

  const begin = (e) => {
    if (disabled) return
    const t = e.touches[0]
    start.current = { x: t.clientX, y: t.clientY, base: open ? -ACTION_W : 0 }
    offset.current = start.current.base
    axis.current = null
    setDragging(true)
  }

  const move = (e) => {
    if (!start.current) return
    const t = e.touches[0]
    const mx = t.clientX - start.current.x
    const my = t.clientY - start.current.y

    if (axis.current === null) {
      if (Math.abs(mx) < 10 && Math.abs(my) < 10) return // not enough to judge
      axis.current = Math.abs(mx) > Math.abs(my) ? 'x' : 'y'
    }
    if (axis.current === 'y') return // it's a scroll; leave it alone

    let next = start.current.base + mx
    if (next > 0) next = 0                                   // never past closed
    if (next < -ACTION_W) next = -ACTION_W - (-ACTION_W - next) * 0.28 // resist
    offset.current = next
    setDx(next)
  }

  const end = () => {
    if (!start.current) return
    const settled = offset.current <= -THRESHOLD
    setOpen(settled)
    offset.current = settled ? -ACTION_W : 0
    setDx(0)
    setDragging(false)
    start.current = null
    axis.current = null
  }

  const shift = dragging ? dx : (open ? -ACTION_W : 0)

  return (
    <div className={`swipe${open ? ' is-open' : ''}`}>
      <button className="swipe-action" tabIndex={open ? 0 : -1} aria-hidden={!open}
              onClick={() => { setOpen(false); offset.current = 0; onAction?.() }}>
        {actionLabel}
      </button>

      <div className={`swipe-surface${dragging ? ' dragging' : ''}`}
           style={{ transform: `translate3d(${shift}px,0,0)` }}
           onTouchStart={begin} onTouchMove={move} onTouchEnd={end} onTouchCancel={end}>
        {children}
      </div>

      {/* Closes an open row by tapping anywhere else on it, the way a phone
          list is expected to behave. */}
      {open && (
        <button className="swipe-scrim" aria-label="Close actions"
                onClick={() => { setOpen(false); offset.current = 0 }} />
      )}
    </div>
  )
}
